import { ComponentType, MutableRefObject, SVGProps } from "react";

import {
    RiChatNewLine,
    RiEditBoxLine,
    RiPriceTag3Line,
    RiEyeLine,
    RiStarLine,
    RiStickyNoteLine,
    RiCheckboxMultipleLine,
    RiUserFollowLine,
    RiListCheck3,
    RiTaskLine,
    RiCloseCircleLine,
    RiShuffleLine,
    RiRobot2Line,
    RiCalendarEventLine,
    RiAlarmLine,
    RiRestartLine,
    RiUserAddLine,
    RiRefundLine,
    RiCalendarCheckLine,
    RiTeamLine,
    RiDiscussLine,
    RiMailSendFill,
    RiUserSharedLine,
    RiIndeterminateCircleLine,
    RiLoopLeftLine,
    RiPauseCircleLine,
    RiBookOpenLine,
    RiChatSmile2Line,
    RiRefreshLine,
    RiFlag2Line,
    RiErrorWarningLine,
    RiAttachmentLine,
    RiChatCheckLine,
    RiBarChartBoxLine,
    RiFileEditLine,
    RiPriceTag2Line,
    RiAlertLine,
    RiArrowUpDoubleLine,
    RiFileAddLine,
    RiFontSize2,
    RiLoopRightLine,
    RiMegaphoneLine,
    RiFocus2Line,
    RiMailLine,
    RiProfileLine,
    RiQuestionAnswerLine,
    RiFlashlightLine,
    RiPlug2Line,
    RiCodeBoxLine,
    RiFilePaperLine,
    RiCheckboxCircleLine,
    RiListUnordered,
    RiForbidLine,
    RemixiconComponentType,
} from "@remixicon/react";

import { TbNumber123 } from "react-icons/tb";
import { Node, getOutgoers, Edge } from "@xyflow/react";
import { ReactComponent as RocketIcon } from "../../../assets/icons/rocket-new.svg";
import StraightThenCurvedEdge from "../components/edges/StraightThenCurvedEdge";
import CurvedEdge from "../components/edges/CurvedEdge";

import BaseActionNode from "../components/nodes/BaseActionNode";
import NewActionNode from "../components/nodes/NewActionNode";
import BaseGroupNode from "../components/nodes/BaseGroupNode";
import SubactionGroupNode from "../components/nodes/SubactionGroupNode";
import EmptyFlowNode from "../components/nodes/EmptyFlowNode";
import InitialActionNode from "../components/nodes/InitialActionNode";
import { ActionContextType } from "../../../__generated__/api";
import GhostActionNode from "../components/nodes/GhostActionNode";

export enum GROUP {
    ACTIONS = "urn:fq:workflow-service-action-child:(urn:fq:workflow-service-action:(urn:fq:workflow-service:system,request-created),actions)",
    ACTIONS_CREATING = "urn:fq:workflow-service-action-child:(urn:fq:workflow-service-action:(urn:fq:workflow-service:system,request-creating),actions)",
    NO_MATCH = "urn:fq:workflow-service-action-child:(urn:fq:workflow-service-action:(urn:fq:workflow-service:system,condition),no-match)",
    MATCH = "urn:fq:workflow-service-action-child:(urn:fq:workflow-service-action:(urn:fq:workflow-service:system,condition),match)",
    CONDITION = "condition",
    START = "urn:fq:workflow-service-action-child:(urn:fq:workflow-service-action:(urn:fq:workflow-service:system,trigger),start)",
    ON_STATUS_UPDATED = "urn:fq:workflow-service-action-child:(urn:fq:workflow-service-action:(urn:fq:workflow-service:system,on-status-updated),actions)",
    WEBHOOK_ACTIONS = "urn:fq:workflow-service-action-child:(urn:fq:workflow-service-action:(urn:fq:workflow-service:system,webhook),actions)",
    REJECTED = "urn:fq:workflow-service-action-child:(urn:fq:workflow-service-action:(urn:fq:workflow-service:system,create-subtask),REJECTED)",
    FINISHED = "urn:fq:workflow-service-action-child:(urn:fq:workflow-service-action:(urn:fq:workflow-service:system,create-subtask),FINISHED)",
    NOT_APPLICABLE = "urn:fq:workflow-service-action-child:(urn:fq:workflow-service-action:(urn:fq:workflow-service:system,create-subtask),NOT_APPLICABLE)",
    MESSAGE_ARRIVES = "urn:fq:workflow-service-action-child:(urn:fq:workflow-service-action:(urn:fq:workflow-service:system,on-new-message),actions)",
    DEFAULT = "default-group",
    SUBACTION = "subaction",
}

export const GROUP_ID_TYPE_MAP: Record<string, string> = {
    [GROUP.ACTIONS]: "actions",
    [GROUP.ACTIONS_CREATING]: "actions-creating",
    [GROUP.NO_MATCH]: "no-match",
    [GROUP.MATCH]: "match",
    [GROUP.CONDITION]: "condition",
    [GROUP.START]: "start",
    [GROUP.ON_STATUS_UPDATED]: "on-status-updated",
    [GROUP.WEBHOOK_ACTIONS]: "webhook-actions",
    [GROUP.REJECTED]: "rejected",
    [GROUP.FINISHED]: "finished",
    [GROUP.NOT_APPLICABLE]: "not-applicable",
    [GROUP.MESSAGE_ARRIVES]: "message-arrives",
    [GROUP.SUBACTION]: "subaction",
    [GROUP.DEFAULT]: "default",
};

export const GROUP_ID_ICON_MAP: Record<string, RemixiconComponentType | ComponentType<SVGProps<SVGSVGElement>>> = {
    [GROUP.ACTIONS]: RocketIcon,
    [GROUP.ACTIONS_CREATING]: RocketIcon,
    [GROUP.WEBHOOK_ACTIONS]: RocketIcon,
    [GROUP.MATCH]: RiCheckboxCircleLine,
    [GROUP.FINISHED]: RiCheckboxCircleLine,
    [GROUP.NO_MATCH]: RiCloseCircleLine,
    [GROUP.REJECTED]: RiCloseCircleLine,
    [GROUP.CONDITION]: RiListCheck3,
    [GROUP.SUBACTION]: RiListUnordered,
    [GROUP.NOT_APPLICABLE]: RiForbidLine,
};

export enum ACTION {
    REQUEST_CREATED = "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,request-created)",
    ADD_MESSAGE = "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,add-message)",
    ADD_NOTE = "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,add-note)",
    CONDITION = "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,condition)",
    CREATE_TASK = "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,create-subtask)",
    RUN_DEFLECTION = "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,run-deflection)",
    DELAY_TIME = "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,delay-time)",
    REQUEST_CREATING = "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,request-creating)",
    GENERIC_NO_TICKET = "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,generic-no-ticket)",
    CREATE_SUBTASK = "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,create-subtask)",
    ASSIGN_TO_QUEUE = "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,assign-to-queue)",
    ASSIGN_TO_USER = "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,assign-to-user)",
    SET_TICKET_STATUS_FINISHED = "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,set-ticket-status-finished)",
    SET_TICKET_STATUS_HOLD = "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,set-ticket-status-hold)",
    SET_TICKET_STATUS_IGNORE = "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,set-ticket-status-ignore)",
    SET_TICKET_STATUS_REOPEN = "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,set-ticket-status-reopen)",
    SET_TICKET_PRIORITY = "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,set-ticket-priority)",
    SET_TICKET_SEVERITY = "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,set-ticket-severity)",
    SET_FIELD_VALUE = "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,set-field-value)",
    MAP = "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,map)",
    ADD_PARTICIPANT = "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,add-participant)",
    TRIGGER = "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,trigger)",
    TRIGGER_CONNECTOR = "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,trigger-connector)",
    SET_TICKET_FIELD_VALUE = "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,set-ticket-field-value)",
    SET_TICKET_SLA_TIER = "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,set-ticket-sla-tier)",
    LOOP = "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,loop)",
    LOOP_MULTIPLE_CHOICE = "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,loop-multiple-choice)",
    FORMAT = "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,format)",
    ON_STATUS_UPDATED = "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,on-status-updated)",
    WEBHOOK = "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,webhook)",
    IS_DATE_IN_HOURS_OF_OPERATION = "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,is-date-in-hours-of-operation)",
    SET_TICKET_STATE = "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,set-ticket-state)",
    ON_FIELD_UPDATED = "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,on-field-updated)",
    SET_TICKET_TAGS = "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,set-ticket-tags)",
    SET_REQUESTOR = "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,set-requestor)",
    IS_USER_IN_GROUP = "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,is-user-in-group)",
    ON_NEW_MESSAGE = "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,on-new-message)",
    ON_DUE_DATE_EXPIRED = "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,on-due-date-expired)",
    SEND_FEEDBACK = "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,send-feedback)",
    RUN_ACTION = "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,run-action)",
    SET_REQUEST_TYPE = "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,set-request-type)",
    SEND_EMAIL = "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,send-email)",
    ALLOW_RESTART = "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,allow-restart)",
    CREATE_TICKET = "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,create-ticket)",
    ON_FEEDBACK = "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,on-feedback)",
    SEND_QUESTION = "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,send-question)",
    FILL_OUT_FORM = "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,fill-out-form)",
    ON_STATUS_REOPENED = "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,on-status-reopened)",
    ON_ERROR = "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,on-error)",
    EXECUTE_WITH_COUNT = "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,execute-with-count)",
    ON_EXTERNAL_TICKET = "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,on-external-ticket)",
    UNASSIGN = "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,unassign)",
    SET_METRIC = "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,set-metric)",
    GET_USER_BY_EMAIL = "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,get-user-by-email)",
    START_CONVERSATION = "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,start-conversation)",
    CHANNEL_SET_TAGS = "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,channel-set-tags)",
    ADD_PARTICIPANT_THREAD = "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,add-participant-thread)",
    MAP_CHANNEL_MESSAGES = "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,map-channel-messages)",
    FIELD_DISPLAY = "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,field-display)",
    FIELD_REQUIRED = "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,field-required)",
    SALESFORCE = "Salesforce",
    WEBHOOK_SERVICE = "Webhook",
    DEFAULT = "default-action",
    NEW = "new",
    GHOST = "ghost",
}

export enum EDGE {
    CURVED = "curved",
    STRAIGHT = "straight",
    STRAIGHT_THEN_CURVED = "straight-then-curved",
}

export enum NODE_TYPE {
    ACTION_BASE = "action-base",
    GROUP_BASE = "group-base",
    EMPTY_FLOW = "empty-flow",
}

export const nodeTypes = {
    [ACTION.NEW]: NewActionNode,
    [ACTION.GHOST]: GhostActionNode,
    [ACTION.REQUEST_CREATED]: InitialActionNode,
    [ACTION.REQUEST_CREATING]: InitialActionNode,
    [ACTION.GENERIC_NO_TICKET]: InitialActionNode,
    [NODE_TYPE.ACTION_BASE]: BaseActionNode,
    [NODE_TYPE.EMPTY_FLOW]: EmptyFlowNode,
    [GROUP.ACTIONS]: BaseGroupNode,
    [GROUP.ACTIONS_CREATING]: BaseGroupNode,
    [GROUP.CONDITION]: BaseGroupNode,
    [GROUP.NO_MATCH]: BaseGroupNode,
    [GROUP.MATCH]: BaseGroupNode,
    [GROUP.START]: BaseGroupNode,
    [GROUP.ON_STATUS_UPDATED]: BaseGroupNode,
    [GROUP.WEBHOOK_ACTIONS]: BaseGroupNode,
    [GROUP.DEFAULT]: BaseGroupNode,
    [GROUP.REJECTED]: BaseGroupNode,
    [GROUP.FINISHED]: BaseGroupNode,
    [GROUP.NOT_APPLICABLE]: BaseGroupNode,
    [GROUP.MESSAGE_ARRIVES]: BaseGroupNode,
    [GROUP.SUBACTION]: SubactionGroupNode,
};

export const edgeTypes = {
    [EDGE.CURVED]: CurvedEdge,
    [EDGE.STRAIGHT_THEN_CURVED]: StraightThenCurvedEdge,
};

export const ACTION_ICON_MAP = {
    [ACTION.CREATE_TASK]: RiTaskLine,
    [ACTION.ADD_NOTE]: RiStickyNoteLine,
    [ACTION.REQUEST_CREATED]: RiFlashlightLine,
    [ACTION.REQUEST_CREATING]: RiFlashlightLine,
    [ACTION.GENERIC_NO_TICKET]: RiFilePaperLine,
    [ACTION.CONDITION]: RiListCheck3,
    [ACTION.ASSIGN_TO_QUEUE]: RiCheckboxMultipleLine,
    [ACTION.ASSIGN_TO_USER]: RiUserFollowLine,
    [ACTION.SET_TICKET_STATUS_FINISHED]: RiCloseCircleLine,
    [ACTION.SET_TICKET_STATUS_HOLD]: RiPauseCircleLine,
    [ACTION.SET_TICKET_STATUS_IGNORE]: RiIndeterminateCircleLine,
    [ACTION.SET_TICKET_STATUS_REOPEN]: RiBookOpenLine,
    [ACTION.SET_TICKET_PRIORITY]: RiArrowUpDoubleLine,
    [ACTION.SET_TICKET_SEVERITY]: RiAlertLine,
    [ACTION.SET_FIELD_VALUE]: RiEditBoxLine,
    [ACTION.MAP]: RiShuffleLine,
    [ACTION.ADD_MESSAGE]: RiChatNewLine,
    [ACTION.ADD_PARTICIPANT]: RiUserAddLine,
    [ACTION.TRIGGER]: RiFlashlightLine,
    [ACTION.TRIGGER_CONNECTOR]: RiPlug2Line,
    [ACTION.SET_TICKET_FIELD_VALUE]: RiCheckboxMultipleLine,
    [ACTION.DELAY_TIME]: RiCalendarEventLine,
    [ACTION.SET_TICKET_SLA_TIER]: RiAlarmLine,
    [ACTION.LOOP]: RiLoopLeftLine,
    [ACTION.LOOP_MULTIPLE_CHOICE]: RiLoopRightLine,
    [ACTION.FORMAT]: RiFontSize2,
    [ACTION.ON_STATUS_UPDATED]: RiFlag2Line,
    [ACTION.WEBHOOK]: RiCodeBoxLine,
    [ACTION.IS_DATE_IN_HOURS_OF_OPERATION]: RiCalendarCheckLine,
    [ACTION.SET_TICKET_STATE]: RiBookOpenLine,
    [ACTION.ON_FIELD_UPDATED]: RiEditBoxLine,
    [ACTION.SET_TICKET_TAGS]: RiPriceTag2Line,
    [ACTION.SET_REQUESTOR]: RiUserSharedLine,
    [ACTION.IS_USER_IN_GROUP]: RiTeamLine,
    [ACTION.ON_NEW_MESSAGE]: RiChatCheckLine,
    [ACTION.ON_DUE_DATE_EXPIRED]: RiAlarmLine,
    [ACTION.RUN_DEFLECTION]: RiRobot2Line,
    [ACTION.SEND_FEEDBACK]: RiChatSmile2Line,
    [ACTION.RUN_ACTION]: RiFocus2Line,
    [ACTION.SET_REQUEST_TYPE]: RiFileEditLine,
    [ACTION.SEND_EMAIL]: RiMailLine,
    [ACTION.ALLOW_RESTART]: RiRestartLine,
    [ACTION.CREATE_TICKET]: RiFileAddLine,
    [ACTION.ON_FEEDBACK]: RiChatSmile2Line,
    [ACTION.SEND_QUESTION]: RiQuestionAnswerLine,
    [ACTION.FILL_OUT_FORM]: RiProfileLine,
    [ACTION.ON_STATUS_REOPENED]: RiRefreshLine,
    [ACTION.ON_ERROR]: RiErrorWarningLine,
    [ACTION.EXECUTE_WITH_COUNT]: TbNumber123,
    [ACTION.ON_EXTERNAL_TICKET]: RiAttachmentLine,
    [ACTION.UNASSIGN]: RiRefundLine,
    [ACTION.SET_METRIC]: RiBarChartBoxLine,
    [ACTION.GET_USER_BY_EMAIL]: RiMailSendFill,
    [ACTION.START_CONVERSATION]: RiChatNewLine,
    [ACTION.CHANNEL_SET_TAGS]: RiPriceTag3Line,
    [ACTION.ADD_PARTICIPANT_THREAD]: RiDiscussLine,
    [ACTION.MAP_CHANNEL_MESSAGES]: RiMegaphoneLine,
    [ACTION.FIELD_DISPLAY]: RiEyeLine,
    [ACTION.FIELD_REQUIRED]: RiStarLine,
};

export const CREATING_ID = "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,request-creating)";
export const CREATED_ID = "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,request-created)";
export const GENERAL_ID = "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,generic-no-ticket)";
export const WEBHOOK_ID = "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,webhook)";

export const ACTION_TYPE_MAP: Record<string, ActionContextType> = {
    [CREATING_ID]: ActionContextType.Request,
    [CREATED_ID]: ActionContextType.Ticket,
    [GENERAL_ID]: ActionContextType.GenericNoTicket,
    [WEBHOOK_ID]: ActionContextType.Webhook,
};

export function getGroupEdgeType(nodeType: string) {
    switch (nodeType) {
    case GROUP.ACTIONS:
    case GROUP.ACTIONS_CREATING:
    case GROUP.WEBHOOK_ACTIONS:
    case GROUP.SUBACTION:
    case GROUP.CONDITION:
        return EDGE.STRAIGHT;
    default:
        return EDGE.CURVED;
    }
}

export function deleteChildNodes(node: Node, nodes: Node[], edges: Edge[]): Node[] {
    let remainingNodes = [...nodes];
    const outgoers = getOutgoers(node, nodes, edges);

    outgoers.forEach(outgoer => {
        remainingNodes = deleteChildNodes(outgoer, remainingNodes, edges);
    });

    remainingNodes = remainingNodes.filter(currentNode => currentNode.id !== node.id);
    return remainingNodes;
}

export const GHOST_NODE_PREFIX = "ghost-node-";
export const GHOST_EDGE_PREFIX = "ghost-edge-";

interface InjectGhostProps {
    currentNodes: Node[];
    currentEdges: Edge[];
    ghostParentId: string;
    insertIndex: number;
    lastGhostParentIdRef: MutableRefObject<string | undefined>;
    lastGhostInsertIndexRef: MutableRefObject<number>;
}

export function injectGhost({
    currentNodes,
    currentEdges,
    ghostParentId,
    insertIndex,
    lastGhostParentIdRef,
    lastGhostInsertIndexRef,
}: InjectGhostProps): { nodes: Node[]; edges: Edge[] } {
    // Start from clean lists (strip any previous ghost)
    const filteredNodes = currentNodes.filter(nd => !nd.id.startsWith(GHOST_NODE_PREFIX));
    const filteredEdges = currentEdges.filter(ed => !ed.id.startsWith(GHOST_EDGE_PREFIX));

    const ghostNodeId = `${GHOST_NODE_PREFIX}${ghostParentId}`;
    const ghostNode: Node = {
        id: ghostNodeId,
        type: ACTION.GHOST,
        position: { x: 0, y: 0 },
        data: { id: ghostNodeId, parentId: ghostParentId },
    } as Node;

    let absoluteInsertIndex = lastGhostInsertIndexRef.current;

    if (ghostParentId !== lastGhostParentIdRef.current) {
        const siblingIndexes = filteredNodes
            .map((nodeItem, idx) => ({ nodeItem, idx }))
            .filter(listItem => listItem.nodeItem.data?.parentId === ghostParentId)
            .map(listItem => listItem.idx)
            .sort((aIdx, bIdx) => aIdx - bIdx);

        absoluteInsertIndex = -1;

        if (insertIndex === siblingIndexes.length) {
            if (siblingIndexes.length === 0) {
                const parentPosition = filteredNodes.findIndex(nodeItem => nodeItem.id === ghostParentId);
                absoluteInsertIndex = parentPosition !== -1 ? parentPosition + 1 : filteredNodes.length;
            } else {
                absoluteInsertIndex = Math.max(...siblingIndexes) + 1;
            }
        } else if (insertIndex < siblingIndexes.length) {
            absoluteInsertIndex = siblingIndexes[insertIndex];
        }

        if (absoluteInsertIndex === -1) {
            absoluteInsertIndex = siblingIndexes.length > 0
                ? Math.max(...siblingIndexes) + 1
                : filteredNodes.findIndex(nodeItem => nodeItem.id === ghostParentId) + 1;
        }

        if (absoluteInsertIndex < 0 || absoluteInsertIndex > filteredNodes.length) {
            absoluteInsertIndex = filteredNodes.length;
        }

        /* eslint-disable no-param-reassign */
        lastGhostParentIdRef.current = ghostParentId;
        lastGhostInsertIndexRef.current = absoluteInsertIndex;
    } else if (absoluteInsertIndex < 0 || absoluteInsertIndex > filteredNodes.length) {
        absoluteInsertIndex = Math.min(insertIndex, filteredNodes.length);
        /* eslint-disable no-param-reassign */
        lastGhostInsertIndexRef.current = absoluteInsertIndex;
    }

    if (!filteredNodes.some(nodeItem => nodeItem.id === ghostNodeId)) {
        filteredNodes.splice(absoluteInsertIndex, 0, ghostNode);
    }

    const edgeId = `${GHOST_EDGE_PREFIX}${ghostParentId}`;
    if (!filteredEdges.some(edgeItem => edgeItem.id === edgeId)) {
        filteredEdges.push({
            id: edgeId,
            source: ghostParentId,
            target: ghostNodeId,
            type: EDGE.CURVED,
            data: { sourceId: ghostParentId, targetId: ghostNodeId },
        });
    }

    return { nodes: filteredNodes, edges: filteredEdges };
}
